import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react"
import "../styles/Paper.css"
import { supabase } from "../supabase"

const Paper = ({ isAdmin }) => {

    const { id } = useParams();

    const [source, setSource] = useState({})
    
    // Edit mode state
    const [editMode, setEditMode] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)

    const creditInfo = useMemo(() => {
  return {
    Source: (
      <>
        {source?.Title} by {source?.Authors?.join(", ")},{" "}
        {source?.Journal} ({source?.Date}).<br />
        Available via {source?.FoundJournal}. Licensed under {source?.License}.<br />
        Link:{" "}
        <a
        href={source?.Link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1539a6ff" }} // purple-blue
        >
            {source?.Link}
        </a><br /><br />

        Disclaimer: Spine Commons does not own, did not create, and is not sponsored by the original publication.<br />
        Notice: Spine Commons modifies publications using NLP (Natural Language Processes) algorithms to transform publication texts to simpler language.
      </>
    ),
    "License & Copyright": "All the publications cited and used on this website are open access, and sourced from many different open access repositories including PMC Open Access, arXiv, and more. Each publication has its own license and copyright terms, which can be found in the 'Source' section. For sources that have additional guidelines surrounding programmatic interpretation and downloading of publications, we use the appropriate channels, such as the PMC OAI-PMH, PMC FTP, and PMC Cloud services for the PMC Open Access database.",
    "Important Notice":
      "This webpage is not always correct, and claims made about the hosted publications may be factually incorrect. Spine Commons applies NLP (Natural Language Processes) transformations that can fail or error. Never use this website as the sole source of truth. To get completely accurate information, visit the publications linked."
  };
}, [source]);

    const [selectedInfo, setSelectedInfo] = useState()

    const [title, setTitle] = useState("")
    const [Content, setContent] = useState({"content" : []})
    
    // Original content for cancel/revert
    const [originalTitle, setOriginalTitle] = useState("")
    const [originalContent, setOriginalContent] = useState({"content" : []})

    const [definition, setDefinition] = useState(-1)
    const [expandedHeadings, setExpandedHeadings] = useState({}) // tracks which heading sources are shown
    const [expandedSources, setExpandedSources] = useState({}) // tracks which individual sources are expanded

    // Favorites state
    const [isFavorited, setIsFavorited] = useState(false)
    const [userId, setUserId] = useState(null)
    const [favouritesCount, setFavouritesCount] = useState(0)

    const nav = useNavigate();
    
    useEffect(() => {
        async function fetchInfo() {
      const { data, error } = await supabase
        .from('study_entries')
        .select("*")
        .eq('id', id)
        .single();

      if (error) {
        console.error(error);
      } else {
        setTitle(data.Title);
        setOriginalTitle(data.Title);
        setSource(data.Source)
        setFavouritesCount(data.Favourites || 0)

        if (data.Content) {
            setContent(data.Content)
            setOriginalContent(data.Content)
        }
        
      }
    }

    fetchInfo();
    }, [id])

    // Fetch user's favorite studies on mount
    useEffect(() => {
        const fetchUserFavorites = async () => {
            const { data: { user } } = await supabase.auth.getUser()
            
            if (user) {
                setUserId(user.id)
                
                const { data, error } = await supabase
                    .from('profiles')
                    .select('favourite_studies')
                    .eq('id', user.id)
                    .single()
                
                if (data && !error) {
                    const favorites = data.favourite_studies || []
                    setIsFavorited(favorites.includes(Number(id)))
                }
            }
        }
        
        fetchUserFavorites()
    }, [id])

    // Toggle favorite status
    const toggleFavorite = async () => {
        if (!userId) {
            console.log('User must be logged in to favorite studies')
            return
        }

        // Get current user favorites
        const { data: profileData } = await supabase
            .from('profiles')
            .select('favourite_studies')
            .eq('id', userId)
            .single()
        
        const currentFavorites = profileData?.favourite_studies || []
        const studyIdNum = Number(id)
        const isCurrentlyFavorited = currentFavorites.includes(studyIdNum)
        
        const updatedFavorites = isCurrentlyFavorited
            ? currentFavorites.filter(fid => fid !== studyIdNum)
            : [...currentFavorites, studyIdNum]
        
        const currentFavCount = favouritesCount
        
        // Optimistically update UI
        setIsFavorited(!isCurrentlyFavorited)
        setFavouritesCount(currentFavCount + (isCurrentlyFavorited ? -1 : 1))
        
        // Update user's favorites in database
        const { error: profileError } = await supabase
            .from('profiles')
            .update({ favourite_studies: updatedFavorites })
            .eq('id', userId)
        
        // Update the study's Favourites count
        const newFavCount = Math.max(0, currentFavCount + (isCurrentlyFavorited ? -1 : 1))
        
        const { error: studyError } = await supabase
            .from('study_entries')
            .update({ Favourites: newFavCount })
            .eq('id', id)
        
        if (profileError || studyError) {
            console.error('Error updating favorites:', { profileError, studyError })
            // Revert on error
            setIsFavorited(isCurrentlyFavorited)
            setFavouritesCount(currentFavCount)
        }
    }

    // Update content item at index
    const updateContentItem = useCallback((index, field, value) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            newContent.content[index] = { ...newContent.content[index], [field]: value }
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Add new content item after index
    const addContentItem = useCallback((index, type) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            let newItem = { type }
            if (type === "heading") {
                newItem.content = "New Heading"
            } else if (type === "body") {
                newItem.content = "New paragraph text"
                newItem.display = "inline"
            } else if (type === "space") {
                newItem.space = "bodySpace"
            }
            newContent.content.splice(index + 1, 0, newItem)
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Delete content item at index
    const deleteContentItem = useCallback((index) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            newContent.content.splice(index, 1)
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Move content item up or down
    const moveContentItem = useCallback((index, direction) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            const newIndex = direction === 'up' ? index - 1 : index + 1
            if (newIndex < 0 || newIndex >= newContent.content.length) return prev
            const item = newContent.content.splice(index, 1)[0]
            newContent.content.splice(newIndex, 0, item)
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Add a source to a heading
    const addSource = useCallback((index) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            const sources = newContent.content[index].Source || []
            newContent.content[index] = { 
                ...newContent.content[index], 
                Source: [...sources, 'New source text'] 
            }
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Update a specific source in a heading
    const updateSource = useCallback((index, sourceIndex, value) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            const sources = [...(newContent.content[index].Source || [])]
            sources[sourceIndex] = value
            newContent.content[index] = { ...newContent.content[index], Source: sources }
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Delete a source from a heading
    const deleteSource = useCallback((index, sourceIndex) => {
        setContent(prev => {
            const newContent = { ...prev, content: [...prev.content] }
            const sources = [...(newContent.content[index].Source || [])]
            sources.splice(sourceIndex, 1)
            newContent.content[index] = { 
                ...newContent.content[index], 
                Source: sources.length > 0 ? sources : undefined 
            }
            return newContent
        })
        setHasChanges(true)
    }, [])

    // Save changes to Supabase
    const saveChanges = async () => {
        setIsSaving(true)
        try {
            const { error } = await supabase
                .from('study_entries')
                .update({ 
                    Title: title,
                    Content: Content,
                    last_edited: new Date().toISOString().split('T')[0]
                })
                .eq('id', id)

            if (error) {
                console.error('Error saving:', error)
                alert('Failed to save changes')
            } else {
                setOriginalTitle(title)
                setOriginalContent(Content)
                setHasChanges(false)
                setEditMode(false)
            }
        } catch (err) {
            console.error('Save error:', err)
            alert('Failed to save changes')
        }
        setIsSaving(false)
    }

    // Cancel editing and revert changes
    const cancelEdit = () => {
        setTitle(originalTitle)
        setContent(originalContent)
        setHasChanges(false)
        setEditMode(false)
    }

    const toggleHeadingSources = (index) => {
        console.log('toggleHeadingSources called for index:', index);
        console.log('Current expandedHeadings state:', expandedHeadings);
        setExpandedHeadings(prev => {
            const newState = {
                ...prev,
                [index]: !prev[index]
            };
            console.log('New expandedHeadings state:', newState);
            return newState;
        })
    }

    const toggleSource = (headingIndex, sourceIndex) => {
        const key = `${headingIndex}-${sourceIndex}`
        console.log('toggleSource called for key:', key);
        console.log('Current expandedSources state:', expandedSources);
        setExpandedSources(prev => {
            const newState = {
                ...prev,
                [key]: !prev[key]
            };
            console.log('New expandedSources state:', newState);
            return newState;
        })
    }

    const truncateText = (text, maxLength = 100) => {
        if (text.length <= maxLength) return text
        return text.substring(0, maxLength)
    }

    const scrollToOutcome = () => {
        const el = document.getElementById("outcome") || document.getElementById("outcomes")
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }

    
    // useEffect(() => {
    //     const adding = toDisplay.split("")

    //     if (toDisplay.includes(displayed)) {
    //         adding = toDisplay
    //         .split("")
    //         .filter(char => !displayed.includes(char))
    //     }
    //     else {
    //         setDisplayed("")
    //     }

    //     for (let i = 0; i < adding.length; i++) {
    //         setDisplayed(displayed + adding[i])
    //     }
    // }, [toDisplay])

    return (
            <div className="mainPaper">
                        {/* Admin Edit Controls */}
                        {isAdmin && (
                            <div className="adminControls">
                                {!editMode ? (
                                    <button className="editModeBtn" onClick={() => setEditMode(true)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                        Edit Document
                                    </button>
                                ) : (
                                    <div className="editModeActions">
                                        <button 
                                            className="saveBtn" 
                                            onClick={saveChanges} 
                                            disabled={isSaving || !hasChanges}
                                        >
                                            {isSaving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button className="cancelBtn" onClick={cancelEdit}>
                                            Cancel
                                        </button>
                                        {hasChanges && <span className="unsavedIndicator">Unsaved changes</span>}
                                    </div>
                                )}
                            </div>
                        )}
                
                        {editMode ? (
                            <input 
                                type="text"
                                className="headingElem editableTitle"
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value)
                                    setHasChanges(true)
                                }}
                            />
                        ) : (
                            <div className="titleRow">
                                <h1 className="headingElem">
                                    {title}
                                </h1>
                                <button 
                                    className={`favorite-button-paper ${userId === null ? "disabled" : "activated"}`}
                                    onClick={toggleFavorite}
                                    disabled={userId === null}
                                    aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                                >
                                    <svg 
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill={isFavorited ? "#4e9bffff" : "none"}
                                        stroke={isFavorited ? "#0a2c55ff" : "#666"}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                    <span className="fav-count">{favouritesCount}</span>
                                </button>
                            </div>
                        )}
                        <div className="credits">
                            {Object.keys(creditInfo).map((title, i) => (
                                <div className={`accordian ${selectedInfo === title ? "active" : "closed"}`} onClick={() => {selectedInfo !== title ? setSelectedInfo(title) : setSelectedInfo(null)}}>
                                    <h2 className="accTitle">{title}</h2>
                                    <h2 className="accActive">{selectedInfo === title ? "-" : "+" }</h2>
                                </div>
                            ))}
                            <h2 className="accTitle documentsHome" onClick={() => nav("/documents")}>Return Home</h2>
                            <button
                                type="button"
                                className="accTitle documentsHome accTitleButton"
                                onClick={scrollToOutcome}
                            >
                                Results
                            </button>
                        </div>
                        <h2 className={`creditText ${selectedInfo ? "active" : "closed"}`}>
                            {creditInfo[selectedInfo]}
                        </h2>
                        <hr className="headingDivider" />
                        {/* Add item at top button in edit mode */}
                        {editMode && (
                            <div className="addItemControls topAddControls">
                                <button className="addItemBtn" onClick={() => addContentItem(-1, 'heading')}>+ Heading</button>
                                <button className="addItemBtn" onClick={() => addContentItem(-1, 'body')}>+ Body</button>
                                <button className="addItemBtn" onClick={() => addContentItem(-1, 'space')}>+ Space</button>
                            </div>
                        )}
                        {/* Content = {type: "doc", content: [{type:... content:...}, {}...]} */}
                        {Content.content?.map((newObj, i) => {  
                            // newObj = {type: ex: "heading", content: ex: "this is a big bold heading"} 
                            // (element of content array of highest parent obj)
                            let space = ""
                                    const inline = newObj.display === "inline"
                                    if (i > 0 && inline) {
                                        const last = Content.content[i - 1]
                                        if (last.display) {
                                            if (last.display === "inline") {
                                                space = " "
                                            }   
                                        }
                                    }
                            
                            // Edit mode wrapper for each content item - COMPACT version
                            const editWrapper = (children) => {
                                if (!editMode) return children
                                return (
                                    <div className="editableItemWrapper" key={`edit-${i}`}>
                                        <div className="editItemRow">
                                            <div className="editItemControls">
                                                <button 
                                                    className="moveBtn" 
                                                    onClick={() => moveContentItem(i, 'up')}
                                                    disabled={i === 0}
                                                    title="Move up"
                                                >↑</button>
                                                <button 
                                                    className="moveBtn" 
                                                    onClick={() => moveContentItem(i, 'down')}
                                                    disabled={i === Content.content.length - 1}
                                                    title="Move down"
                                                >↓</button>
                                                <span className="itemTypeLabel">{newObj.type.charAt(0).toUpperCase()}</span>
                                            </div>
                                            {children}
                                            <div className="editItemActions">
                                                <button className="addItemBtn small" onClick={() => addContentItem(i, 'heading')} title="Add heading">+H</button>
                                                <button className="addItemBtn small" onClick={() => addContentItem(i, 'body')} title="Add body">+B</button>
                                                <button className="addItemBtn small" onClick={() => addContentItem(i, 'space')} title="Add space">+S</button>
                                                <button 
                                                    className="deleteBtn" 
                                                    onClick={() => deleteContentItem(i)}
                                                    title="Delete"
                                                >×</button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }
                            
                            switch (newObj.type) {

                                case "heading":
                                    const hasSource = newObj.Source && newObj.Source.length > 0;
                                    const hasLink = newObj.link;
                                    const isOutcome = newObj.outcomes    
                                    
                                    if (editMode) {
                                        return editWrapper(
                                            <div className="editableHeading" key={`heading-${i}`}>
                                                <input
                                                    type="text"
                                                    className="editableHeadingInput"
                                                    value={newObj.content}
                                                    onChange={(e) => updateContentItem(i, 'content', e.target.value)}
                                                    placeholder="Heading text..."
                                                />
                                                <div className="headingOptions">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!newObj.outcomes}
                                                            onChange={(e) => updateContentItem(i, 'outcomes', e.target.checked)}
                                                        />
                                                        Outcomes
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="editableLinkInput"
                                                        value={newObj.link || ''}
                                                        onChange={(e) => updateContentItem(i, 'link', e.target.value)}
                                                        placeholder="Link URL"
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="addSourceBtn" 
                                                        onClick={() => addSource(i)}
                                                    >
                                                        + Source
                                                    </button>
                                                </div>
                                                {newObj.Source && newObj.Source.length > 0 && (
                                                    <div className="editableSourcesList">
                                                        {newObj.Source.map((src, srcIdx) => (
                                                            <div className="editableSourceItem" key={srcIdx}>
                                                                <span className="sourceNum">{srcIdx + 1}</span>
                                                                <textarea
                                                                    className="editableSourceInput"
                                                                    value={src}
                                                                    onChange={(e) => updateSource(i, srcIdx, e.target.value)}
                                                                    placeholder="Source text..."
                                                                    rows={2}
                                                                />
                                                                <button 
                                                                    type="button" 
                                                                    className="deleteSourceBtn" 
                                                                    onClick={() => deleteSource(i, srcIdx)}
                                                                    title="Remove source"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    }
                                    
                                    return (
                                        <>
                                            <div className="heading" id={isOutcome ? "outcome" : undefined}>
                                                {isOutcome && <span id="outcomes"></span>}
                                                {hasLink && !hasSource ? (
                                                    <a href={newObj.link} target="_blank" rel="noopener noreferrer">
                                                        <h1 className="headingText linkColor">
                                                            {newObj.content}
                                                        </h1>
                                                    </a>
                                                ) : hasLink && hasSource ? (
                                                    <a href={newObj.link} target="_blank" rel="noopener noreferrer">
                                                        <h1 
                                                            className="headingText linkColor hasSource"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                toggleHeadingSources(i);
                                                            }}
                                                        >
                                                            {newObj.content}
                                                            <span className="sourceIndicator">
                                                                [{newObj.Source.length}]
                                                            </span>
                                                        </h1>
                                                    </a>
                                                ) : hasSource ? (
                                                    <h1 
                                                        className="headingText hasSource"
                                                        onClick={() => {
                                                            console.log('Heading clicked, toggling sources for index:', i);
                                                            toggleHeadingSources(i);
                                                        }}
                                                    >
                                                        {newObj.content}
                                                        <span className="sourceIndicator">
                                                            [{newObj.Source.length}]
                                                        </span>
                                                    </h1>
                                                ) : (
                                                    <h1 className="headingText">
                                                        {newObj.content}
                                                    </h1>
                                                )}
                                            </div>
                                            {(() => {
                                                return null;
                                            })()}
                                            {hasSource && expandedHeadings[i] && (
                                                <div className="sourcesContainer">
                                                    <div className="sourcesHeader">Evidence</div>
                                                    {newObj.Source.map((sourceText, sourceIdx) => {
                                                        const key = `${i}-${sourceIdx}`
                                                        const isExpanded = expandedSources[key]
                                                        const shouldTruncate = sourceText.length > 100
                                                        
                                                        return (
                                                            <div 
                                                                key={sourceIdx}
                                                                className={`sourceItem ${shouldTruncate ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}
                                                                onClick={() => {
                                                                    if (shouldTruncate) {
                                                                        console.log('Source clicked:', i, sourceIdx);
                                                                        toggleSource(i, sourceIdx);
                                                                    }
                                                                }}
                                                            >
                                                                <span className="sourceNumber">{sourceIdx + 1}</span>
                                                                <p className="sourceText">
                                                                    {isExpanded || !shouldTruncate 
                                                                        ? sourceText 
                                                                        : `${truncateText(sourceText)}...`
                                                                    }
                                                                </p>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    );

                                case "body":
                                    if (editMode) {
                                        return editWrapper(
                                            <div className="editableBody" key={`body-${i}`}>
                                                <textarea
                                                    className="editableBodyInput"
                                                    value={newObj.content}
                                                    onChange={(e) => updateContentItem(i, 'content', e.target.value)}
                                                    placeholder="Body text..."
                                                    rows={3}
                                                />
                                                <div className="bodyOptions">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={newObj.display === "inline"}
                                                            onChange={(e) => updateContentItem(i, 'display', e.target.checked ? "inline" : undefined)}
                                                        />
                                                        Inline
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!newObj.bullet}
                                                            onChange={(e) => updateContentItem(i, 'bullet', e.target.checked)}
                                                        />
                                                        Bullet
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="editableDropdownInput"
                                                        value={newObj.dropdown || ''}
                                                        onChange={(e) => updateContentItem(i, 'dropdown', e.target.value)}
                                                        placeholder="Dropdown definition (optional)"
                                                    />
                                                </div>
                                            </div>
                                        )
                                    }
                                    
                                    return (
                                        <>
                                            <p 
                                                className={`paperBody ${inline ? "inline" : ""} ${space ? "inlineMargin" : ""}`} 
                                                onClick={() => {
                                                    if (newObj.dropdown) {
                                                        setDefinition(definition === i ? -1 : i)
                                                    }
                                                }}
                                            >
                                                {newObj.bullet && <span className="bullet">• </span>}
                                                <span className={`${newObj.dropdown ? "Dropdown" : ""} ${definition === i ? "activeDropdown" : ""}`}>
                                                    {newObj.content}
                                                </span>
                                            </p>
                                            <p className={`definition ${definition === i ? "activeDef" : "RegressedDef"}`}>
                                                {" "}{newObj.dropdown}
                                            </p>
                                        </>
                                    );
                                
                                case "space":
                                    if (editMode) {
                                        return editWrapper(
                                            <div className="editableSpace" key={`space-${i}`}>
                                                <select
                                                    value={newObj.space || 'bodySpace'}
                                                    onChange={(e) => updateContentItem(i, 'space', e.target.value)}
                                                >
                                                    <option value="bodySpace">Body Space</option>
                                                    <option value="sectionSpace">Section Space</option>
                                                </select>
                                            </div>
                                        )
                                    }
                                    console.log("space")
                                    return (
                                        <div className={newObj.space}></div>
                                    )
                                        
                                default:
                                    return null;
                            }
                        })}
            </div>
    )
}

export default Paper;